<template>
  <div class="ai-assistant-page">
    <!-- Tab切换导航 -->
    <div class="custom-navbar">
      <div class="nav-tabs">
        <div
          class="nav-tab"
          v-for="(item, index) in tabs"
          :key="'tab' + index"
          :class="{ active: item.key === currentTab }"
          @click="switchTab(item.key)"
        >
          {{ item.name }}
        </div>
      </div>
    </div>

    <!-- 内容区域 -->
    <div class="content-container">
      <!-- AI提示词 - 全局显示 -->
      <div class="ai-tag global-ai-tag">
        <span v-if="currentTab === 'reading'">*AI生成,仅供参考,示范作答字数不一定对,保持独立思考</span>
        <span v-else>*AI生成,仅供参考,请保持独立思考</span>
      </div>

      <!-- 踩点对比分析页面 -->
      <div v-show="currentTab === 'analysis'" class="ai-comprehensive-answer-section">
        <!-- 得分点清单卡片 -->
        <div class="comprehensive-answer-card">
          <div class="comprehensive-header">
            <div class="comprehensive-title module-title">得分点清单</div>
          </div>
          <div class="comprehensive-content">
            <!-- 如果有生成的得分点清单,显示实际数据 -->
            <div v-if="aiAnalysisResults.scoringChecklist" class="scoring-checklist-content">
              <div v-html="renderMarkdown(aiAnalysisResults.scoringChecklist)" class="analysis-markdown"></div>
            </div>
            <!-- 加载中状态 -->
            <div v-else-if="!questionAiAnalysisLoaded" class="placeholder-content">
              <div class="placeholder-text">加载中...</div>
            </div>
            <!-- 否则显示占位提示 -->
            <div v-else class="placeholder-content">
              <div class="placeholder-text">暂无得分点清单</div>
            </div>
          </div>
        </div>

        <!-- AI分析区域 -->
        <div class="comprehensive-answer-card">
          <div class="comprehensive-header">
            <div class="comprehensive-title module-title">踩点对比</div>
            <div class="percentage-circle">
              <div class="circle-container">
                <div class="progress-ring" :style="{ '--progress-angle': (estimatedScoreRate * 2.7) + 'deg' }"></div>
                <div class="circle-content">
                  <div class="percent-text">{{ estimatedScore }}</div>
                  <div class="percent-label">总分 {{ totalScore }}</div>
                </div>
              </div>
            </div>
          </div>

          <!-- 用户答案区域 -->
          <div class="user-answer-content" v-if="userInputAnswer">
            <!-- 使用AnnotationHighlighter组件显示交互式标注 -->
            <AnnotationHighlighter
              v-if="parsedAnnotations.length > 0"
              :content="cleanUserAnswer"
              :annotations="parsedAnnotations"
              :selected-annotation-id="currentAnnotationIndex"
              @annotation-click="handleAnnotationClick"
            />
            <!-- 否则显示原始用户答案 -->
            <div v-else class="plain-text">{{ userInputAnswer }}</div>
          </div>
        </div>

        <!-- 遗漏得分点卡片 -->
        <div v-if="parsedAnnotations.length > 0" class="comprehensive-answer-card">
          <div class="comprehensive-header">
            <div class="comprehensive-title module-title">遗漏得分点</div>
          </div>
          <div class="comprehensive-content">
            <!-- 如果有遗漏得分点,显示实际数据 -->
            <div v-if="missedScoringPoints.length > 0" class="missed-points-list">
              <div v-for="(point, index) in missedScoringPoints" :key="index" class="missed-point-item">
                <div class="point-header">
                  <div class="point-label">
                    <span class="point-number">得分点{{ point.number }}</span>
                    <span class="point-prefix">:</span>
                  </div>
                  <div class="point-content-wrapper">
                    <span class="point-content">{{ point.content }}</span>
                  </div>
                </div>
                <div class="point-suggestion">
                  <span class="suggestion-label">建议:</span>
                  <span class="suggestion-text">{{ point.suggestion }}</span>
                </div>
              </div>
            </div>
            <!-- 否则显示占位提示 -->
            <div v-else class="placeholder-content">
              <div class="placeholder-text">暂无遗漏点</div>
            </div>
          </div>
        </div>
      </div>

      <!-- 材料领读页面 -->
      <div v-show="currentTab === 'reading'" class="reading-section">
        <div class="reading-content">
          <!-- 始终显示结构化卡片 -->
          <div class="structured-reading">
            <!-- 步骤条导航 -->
            <div class="reading-steps">
              <template v-for="(step, index) in readingSteps">
                <div
                  :key="index"
                  class="step-item"
                  :class="{ active: currentReadingStep === index }"
                  @click="scrollToReadingCard(index)"
                >
                  <div class="step-number">{{ index + 1 }}</div>
                  <div class="step-label">{{ step.label }}</div>
                </div>
                <div v-if="index < readingSteps.length - 1" :key="'line-' + index" class="step-line"></div>
              </template>
            </div>

            <!-- 1. 材料梳理 -->
            <div class="reading-card" ref="materialAnalysisCard">
              <div class="reading-card-header">
                <div class="module-title">材料梳理</div>
              </div>
              <div class="reading-card-content">
                <!-- 如果有数据则显示 -->
                <div v-if="aiAnalysisResults.materialReading.materialAnalysis.length > 0">
                  <div
                    v-for="(item, index) in aiAnalysisResults.materialReading.materialAnalysis"
                    :key="'ma-' + index"
                    class="material-item"
                  >
                    <div class="material-paragraph-label">段落 {{ item.paragraph }}</div>
                    <div class="material-original-text">{{ item.originalText }}</div>
                    <div v-if="item.keyPoints && item.keyPoints.length > 0" class="material-key-points">
                      <span class="key-points-label">勾画要点:</span>
                      <span
                        v-for="(point, pIndex) in item.keyPoints"
                        :key="'kp-' + pIndex"
                        class="key-point-tag"
                      >{{ point }}</span>
                    </div>
                    <div v-if="item.summary" class="material-summary">
                      <span class="summary-label">初步归纳:</span>
                      <span class="summary-text">{{ item.summary }}</span>
                    </div>
                  </div>
                </div>
                <!-- 无数据显示占位符 -->
                <div v-else class="placeholder-content">
                  <div class="placeholder-text">暂无材料梳理内容</div>
                </div>
              </div>
            </div>

            <!-- 2. 答题要点 -->
            <div class="reading-card" ref="answerPointsCard">
              <div class="reading-card-header">
                <div class="module-title">答题要点</div>
              </div>
              <div class="reading-card-content">
                <!-- 如果有数据则显示 -->
                <div v-if="aiAnalysisResults.materialReading.answerPoints.length > 0">
                  <div
                    v-for="(item, index) in aiAnalysisResults.materialReading.answerPoints"
                    :key="'ap-' + index"
                    class="answer-point-item"
                  >
                    <div class="point-dimension">{{ item.dimension }}</div>
                    <div v-if="item.sources" class="point-sources">
                      <span class="sources-label">来源:</span>
                      <span class="sources-text">{{ item.sources }}</span>
                    </div>
                    <div class="point-content-text">{{ item.content }}</div>
                  </div>
                </div>
                <!-- 无数据显示占位符 -->
                <div v-else class="placeholder-content">
                  <div class="placeholder-text">暂无答题要点内容</div>
                </div>
              </div>
            </div>

            <!-- 3. 示范作答 -->
            <div class="reading-card" ref="sampleAnswerCard">
              <div class="reading-card-header">
                <div class="module-title">示范作答</div>
              </div>
              <div class="reading-card-content">
                <!-- 如果有数据则显示 -->
                <div v-if="aiAnalysisResults.materialReading.sampleAnswer" class="sample-answer-text">
                  {{ aiAnalysisResults.materialReading.sampleAnswer }}
                </div>
                <!-- 无数据显示占位符 -->
                <div v-else class="placeholder-content">
                  <div class="placeholder-text">暂无示范作答内容</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- 优化建议页面 -->
      <div v-show="currentTab === 'optimization'" class="optimization-section">
        <!-- 能力雷达图 -->
        <div class="ability-radar">
          <div class="module-header">
            <div class="module-title">能力雷达图</div>
          </div>
          <div class="radar-content">
            <canvas ref="radarCanvas" class="radar-canvas"></canvas>
          </div>
        </div>

        <!-- 个性化推荐 -->
        <div class="personalized-recommendation">
          <div class="module-header">
            <div class="module-title">个性化推荐</div>
          </div>
          <!-- 显示个性化推荐内容 -->
          <div class="recommendation-content">
            <!-- 内容显示 -->
            <div v-if="aiAnalysisResults.personalizedRecommendation">
              <div v-html="renderMarkdown(aiAnalysisResults.personalizedRecommendation)" class="recommendation-markdown"></div>
            </div>
            <!-- 占位符 -->
            <div v-else class="placeholder-content">
              <div class="placeholder-text">暂无个性化推荐</div>
            </div>
          </div>
        </div>

        <!-- 结构化建议 -->
        <div class="structured-advice">
          <div class="module-header structured-header">
            <div class="module-title">结构化建议</div>
          </div>
          <!-- 显示结构化建议内容 -->
          <div class="structured-content">
            <!-- 内容显示 -->
            <div v-if="aiAnalysisResults.structuredAdvice">
              <div v-html="renderMarkdown(aiAnalysisResults.structuredAdvice)" class="optimization-markdown"></div>
            </div>
            <!-- 占位符 -->
            <div v-else class="placeholder-content">
              <div class="placeholder-text">暂无结构化建议</div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- 踩点详情抽屉 -->
    <el-drawer
      :visible.sync="showAnnotationModal"
      direction="btt"
      size="75%"
      :show-close="false"
      :modal="true"
      :modal-append-to-body="true"
      :append-to-body="true"
      custom-class="annotation-drawer"
    >
      <div slot="title" class="drawer-header">
        <span class="drawer-title">踩点详情</span>
      </div>
      <div class="drawer-content">
        <!-- 批注详情容器 -->
        <div class="annotation-tooltip-container">
          <!-- 当前选中的批注内容 -->
          <div v-if="currentAnnotation" class="tooltip-content">
            <!-- 原始内容 -->
            <div v-if="currentAnnotation.original_content" class="original-content">
              <span class="content-label">原文:</span>
              <span class="content-text">{{ currentAnnotation.original_content }}</span>
            </div>

            <!-- 修正内容 -->
            <div
              v-if="currentAnnotation.type === 'correction' && currentAnnotation.correction_content"
              class="correction-content"
            >
              <span class="content-label">修正:</span>
              <span class="content-text">{{ currentAnnotation.correction_content }}</span>
            </div>

            <!-- 建议 -->
            <div
              v-if="currentAnnotation.suggestion && currentAnnotation.suggestion.trim()"
              class="suggestion-content"
            >
              <span class="suggestion-label">详细建议:</span>
              <div class="suggestion-list">
                <div v-for="(item, index) in currentSuggestionItems" :key="index" class="suggestion-item">
                  <span class="suggestion-text">{{ item }}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </el-drawer>
  </div>
</template>

<script>
import AnnotationHighlighter from './AnnotationHighlighter.vue'
import { getQuestionAiAnalysisList } from '@/api/index'

export default {
  name: 'AiAssistant',
  components: {
    AnnotationHighlighter
  },
  props: {
    // 接收从父组件传入的答案数据
    answerData: {
      type: Object,
      default: () => ({})
    }
  },
  data() {
    return {
      currentTab: 'analysis',
      tabs: [
        { name: '踩点对比', key: 'analysis' },
        { name: '优化建议', key: 'optimization' },
        { name: '材料领读', key: 'reading' }
      ],
      coveragePercent: 0,
      // AI分析相关
      aiAnalysisResults: {
        scoringChecklist: '', // 得分点清单
        answerComparison: '', // 答案对比结果
        structuredAdvice: '', // 结构化建议(优化建议tab显示)
        personalizedRecommendation: '', // 个性化推荐(优化建议tab显示)
        materialReadingContent: '', // 材料领读结果(旧格式,保留兼容)
        materialReading: {
          // 材料领读结构化数据(新格式)
          materialAnalysis: [], // 材料梳理(逐段分析)
          answerPoints: [], // 答题要点(整合后的要点)
          sampleAnswer: '' // 示范作答(完整答案)
        },
        radarScores: null // 雷达图维度原始分(AI返回)
      },
      // 批注数据(用于AnnotationHighlighter组件)
      parsedAnnotations: [], // 解析后的批注数组
      currentAnnotationIndex: 0, // 当前选中的批注索引
      showAnnotationModal: false, // 是否显示踩点详情弹窗
      missedScoringPoints: [], // 遗漏得分点列表
      cleanUserAnswer: '', // 清理后的用户答案(纯文本)
      questionAiAnalysisLoaded: false, // 是否已从接口载入内容
      userInputAnswer: '', // 用户输入的答案
      // 材料领读步骤条
      currentReadingStep: 0, // 当前选中的材料领读步骤
      readingSteps: [
        { label: '材料梳理', id: 'material-analysis-card' },
        { label: '答题要点', id: 'answer-points-card' },
        { label: '示范作答', id: 'sample-answer-card' }
      ],
      // 默认雷达图配置(降级使用)
      defaultRadarConfig: {
        indicators: ['要点捕捉度', '扩充准确性', '逻辑层级', '表述规范性'],
        score: [0, 0, 0, 0]
      },
      dynamicRadarData: null // 动态雷达图数据,由AI返回的数据生成
    }
  },
  computed: {
    // 当前选中的批注对象
    currentAnnotation() {
      if (!this.parsedAnnotations || this.parsedAnnotations.length === 0) {
        return null
      }
      return this.parsedAnnotations[this.currentAnnotationIndex] || this.parsedAnnotations[0]
    },

    // 当前批注的建议列表(按 | 或 ｜ 分割)
    currentSuggestionItems() {
      if (!this.currentAnnotation || !this.currentAnnotation.suggestion) {
        return []
      }
      const suggestion = this.currentAnnotation.suggestion.trim()
      if (!suggestion) {
        return []
      }
      // 支持中文全角竖线 ｜ 和英文半角竖线 |
      return suggestion.split(/[|｜]/).map(item => item.trim()).filter(item => item.length > 0)
    },

    // 题目总分
    totalScore() {
      // 从题目数据中获取总分,默认为10分
      const score = Number(this.answerData?.score)
      return score > 0 ? score : 10
    },

    // 预估得分
    estimatedScore() {
      if (this.answerData && this.answerData.estimatedScore !== undefined && this.answerData.estimatedScore !== null && this.answerData.estimatedScore !== '') {
        const score = Number(this.answerData.estimatedScore)
        if (!Number.isNaN(score)) {
          return parseFloat(Math.max(0, Math.min(this.totalScore, score)).toFixed(1))
        }
      }
      const normalizedCoverage = Math.max(0, Math.min(100, Number(this.coveragePercent) || 0))
      const score = ((normalizedCoverage / 100) * this.totalScore).toFixed(1)
      return parseFloat(score)
    },

    // 预估得分率
    estimatedScoreRate() {
      if (this.answerData && this.answerData.estimatedScoreRate !== undefined && this.answerData.estimatedScoreRate !== null && this.answerData.estimatedScoreRate !== '') {
        const rate = Number(this.answerData.estimatedScoreRate)
        if (!Number.isNaN(rate)) {
          return Math.max(0, Math.min(100, rate))
        }
      }
      return Math.max(0, Math.min(100, Number(this.coveragePercent) || 0))
    }
  },
  watch: {
    answerData: {
      handler(newVal) {
        if (newVal && Object.keys(newVal).length > 0) {
          this.applyAnswerData(newVal)
        }
      },
      immediate: true,
      deep: true
    }
  },
  mounted() {
    // 组件挂载后,如果已有数据则应用
    if (this.answerData && Object.keys(this.answerData).length > 0) {
      this.applyAnswerData(this.answerData)
    }
  },
  methods: {
    // 应用答案数据
    applyAnswerData(data) {
      if (!data) return

      // 重置状态
      this.parsedAnnotations = []
      this.currentAnnotationIndex = 0
      this.missedScoringPoints = []
      this.cleanUserAnswer = ''

      // 解析AI分析结果
      const storedComparison = data.answerComparison || ''
      let storedRadarScores = data.radarScores || null
      if (typeof storedRadarScores === 'string') {
        try {
          storedRadarScores = JSON.parse(storedRadarScores)
        } catch (error) {
          storedRadarScores = null
        }
      }
      const storedStructuredAdvice = data.structuredAdvice || ''
      const storedPersonalized = data.personalizedRecommendation || ''
      const storedCoverage = Number(data.coveragePercent)

      // 处理遗漏得分点数据
      if (data.missedScoringPoints) {
        try {
          if (typeof data.missedScoringPoints === 'string') {
            this.missedScoringPoints = JSON.parse(data.missedScoringPoints)
          } else if (Array.isArray(data.missedScoringPoints)) {
            this.missedScoringPoints = data.missedScoringPoints
          }
        } catch (error) {
          this.missedScoringPoints = []
        }
      }

      if (storedComparison) {
        this.aiAnalysisResults.answerComparison = storedComparison

        const annotations = this.extractStoredAnnotations(storedComparison)
        if (annotations.length > 0) {
          // 过滤有效批注并去重
          let validAnnotations = annotations.filter(
            ann => ann && ann.type && ann.original_content && ann.suggestion
          )

          // 去重:基于 original_content 去重
          const uniqueAnnotations = []
          const seenContent = new Set()

          for (const ann of validAnnotations) {
            const contentKey = `${ann.type}:${ann.original_content}`
            if (!seenContent.has(contentKey)) {
              uniqueAnnotations.push(ann)
              seenContent.add(contentKey)
            }
          }

          this.parsedAnnotations = uniqueAnnotations
          this.currentAnnotationIndex = 0
        } else {
          this.parsedAnnotations = []
          this.currentAnnotationIndex = 0
        }
      } else {
        this.aiAnalysisResults.answerComparison = ''
        this.parsedAnnotations = []
        this.currentAnnotationIndex = 0
      }

      // 直接设置coveragePercent,优先使用后端返回的数据
      if (!Number.isNaN(storedCoverage)) {
        this.coveragePercent = storedCoverage
      } else {
        this.coveragePercent = 0
      }

      if (storedStructuredAdvice) {
        this.aiAnalysisResults.structuredAdvice = storedStructuredAdvice
      }
      if (storedPersonalized) {
        this.aiAnalysisResults.personalizedRecommendation = storedPersonalized
      }
      if (storedRadarScores && typeof storedRadarScores === 'object') {
        this.aiAnalysisResults.radarScores = storedRadarScores
      }

      // 获取用户输入的答案
      const fallbackUserAnswer = typeof data.userAnswer === 'string' ? data.userAnswer : ''
      const userInputAnswerRaw =
        typeof data.userInputAnswer === 'string' ? data.userInputAnswer : fallbackUserAnswer
      if (userInputAnswerRaw) {
        this.userInputAnswer = this.cleanRichText(userInputAnswerRaw)
      } else if (fallbackUserAnswer) {
        this.userInputAnswer = this.cleanRichText(fallbackUserAnswer)
      } else {
        this.userInputAnswer = ''
      }

      // 设置清理后的用户答案(用于AnnotationHighlighter组件)
      this.cleanUserAnswer = this.userInputAnswer || ''

      if (!this.aiAnalysisResults.answerComparison) {
        this.coveragePercent = 0
      }

      // 加载题目AI分析(得分点清单、材料领读)
      this.loadQuestionAiAnalysis(data)
    },

    extractStoredAnnotations(storedComparison) {
      if (!storedComparison || typeof storedComparison !== 'string') {
        return []
      }

      try {
        let cleanComparison = storedComparison.trim()
        if (cleanComparison.startsWith('```')) {
          cleanComparison = cleanComparison.replace(/^```(?:json)?\s*/i, '')
          cleanComparison = cleanComparison.replace(/\s*```$/i, '')
        }

        const parsed = JSON.parse(cleanComparison)
        if (Array.isArray(parsed)) {
          return parsed
        }
        if (parsed && typeof parsed === 'object') {
          if (Array.isArray(parsed.annotations)) {
            return parsed.annotations
          }
          if (Array.isArray(parsed.reviews)) {
            return parsed.reviews
          }
          if (Array.isArray(parsed.items)) {
            return parsed.items
          }
        }
      } catch (error) {
      }

      return []
    },

    // 加载题目AI分析(得分点清单、材料领读)
    async loadQuestionAiAnalysis(data) {
      if (this.questionAiAnalysisLoaded) return

      // 构建查询参数
      const params = {}
      if (data && data.questionId) {
        params.questionId = data.questionId
      }

      try {
        // 调用接口获取题目AI分析数据
        const response = await getQuestionAiAnalysisList(params)
        if (response && response.code === 200) {
          const rows = Array.isArray(response.rows) ? response.rows : []
          if (rows.length > 0) {
            // 如果有 questionId，查找匹配的记录
            let questionAiAnalysis = null
            if (params.questionId) {
              questionAiAnalysis = rows.find(item => String(item.questionId) === String(params.questionId))
            }
            // 如果没找到匹配的或没有 questionId，使用第一条
            if (!questionAiAnalysis) {
              questionAiAnalysis = rows[0]
            }

            // 应用得分点清单
            if (questionAiAnalysis.scoringChecklist) {
              this.aiAnalysisResults.scoringChecklist = questionAiAnalysis.scoringChecklist
            }

            // 应用材料领读数据
            if (questionAiAnalysis.materialReading) {
              this.aiAnalysisResults.materialReadingContent = questionAiAnalysis.materialReading
              // 尝试解析 JSON 格式的材料领读数据
              this.parseMaterialReadingJson(questionAiAnalysis.materialReading)
            }
          }
        }
      } catch (error) {
        console.error('加载题目AI分析失败:', error)
      } finally {
        this.questionAiAnalysisLoaded = true
      }
    },

    // 清理富文本内容,提取纯文本(保留换行)
    cleanRichText(content) {
      if (!content) return ''

      // 如果是数组格式的富文本节点
      if (Array.isArray(content)) {
        return content
          .map(node => {
            if (typeof node === 'string') {
              return node
            } else if (node.type === 'text') {
              return node.text || ''
            } else if (node.children) {
              return this.cleanRichText(node.children)
            }
            return ''
          })
          .join('')
      }

      // 如果是字符串,只做基本的HTML标签清理,保留文本内容和换行
      if (typeof content === 'string') {
        return content
          // 将常见的块级和换行标签替换为换行符
          .replace(/<\/p>/gi, '\n')
          .replace(/<br\s*\/?>/gi, '\n')
          .replace(/<\/div>/gi, '\n')
          .replace(/<br>/gi, '\n')
          // 去除所有HTML标签
          .replace(/<[^>]*>/g, '')
          // 处理HTML实体
          .replace(/&nbsp;/g, ' ')
          .replace(/&lt;/g, '<')
          .replace(/&gt;/g, '>')
          .replace(/&amp;/g, '&')
          .replace(/&quot;/g, '"')
          .replace(/&#39;/g, "'")
          // 去除首尾空白
          .trim()
      }

      return String(content)
    },

    // 切换tab
    switchTab(key) {
      this.currentTab = key

      // 切换到优化建议tab时,绘制雷达图
      if (key === 'optimization') {
        this.$nextTick(() => {
          this.drawRadarChart()
        })
      }
    },

    // 设置雷达图数据(直接使用AI返回的数据)
    setupRadarData() {
      // 如果没有用户答案,所有分数为0
      if (!this.userInputAnswer || !this.userInputAnswer.trim()) {
        return
      }

      // 直接使用 AI 返回的雷达图数据
      const metadataScores = this.aiAnalysisResults.radarScores
      if (metadataScores && typeof metadataScores === 'object' && Object.keys(metadataScores).length > 0) {
        // 从AI数据中获取维度和分数
        const indicators = Object.keys(metadataScores)
        const scores = Object.values(metadataScores).map(score => {
          const raw = Number(score)
          const clamped = Math.max(0, Math.min(10, raw))
          return Number(clamped.toFixed(1))
        })

        // 创建动态雷达图数据
        this.dynamicRadarData = {
          indicators: indicators,
          score: scores
        }

        return
      }
      // 如果 AI 未返回分数,使用默认配置
      this.dynamicRadarData = this.defaultRadarConfig
    },

    // 绘制雷达图
    drawRadarChart() {
      // 先计算评分
      this.setupRadarData()

      const canvas = this.$refs.radarCanvas
      if (!canvas) return

      // 获取设备像素比,解决Canvas模糊问题
      const dpr = window.devicePixelRatio || 1
      const rect = canvas.getBoundingClientRect()

      // 设置Canvas实际分辨率(考虑设备像素比)
      canvas.width = rect.width * dpr
      canvas.height = rect.height * dpr

      const ctx = canvas.getContext('2d')
      // 缩放绘图上下文以匹配高分辨率
      ctx.scale(dpr, dpr)

      // 直接使用计算后的动态雷达图数据,如果没有则使用默认配置
      const radarData = this.dynamicRadarData || this.defaultRadarConfig
      const { indicators, score } = radarData

      this.renderRadarChart(ctx, rect.width, rect.height, indicators, score)
    },

    // 实际绘制雷达图的方法
    renderRadarChart(ctx, width, height, indicators, score) {
      const centerX = width / 2
      const centerY = height / 2
      const radius = Math.min(width, height) * 0.22
      const levels = 5 // 层级数
      const angleStep = (Math.PI * 2) / indicators.length

      // 清空画布
      ctx.clearRect(0, 0, width, height)

      // 设置抗锯齿和图像平滑
      ctx.imageSmoothingEnabled = true
      ctx.imageSmoothingQuality = 'high'

      // 绘制背景网格
      for (let level = 1; level <= levels; level++) {
        const r = (radius / levels) * level
        ctx.beginPath()
        ctx.strokeStyle = '#EEEEEE'
        ctx.lineWidth = 1

        for (let i = 0; i <= indicators.length; i++) {
          const angle = angleStep * i - Math.PI / 2
          const x = centerX + r * Math.cos(angle)
          const y = centerY + r * Math.sin(angle)

          if (i === 0) {
            ctx.moveTo(x, y)
          } else {
            ctx.lineTo(x, y)
          }
        }
        ctx.closePath()
        ctx.stroke()
      }

      // 绘制轴线
      ctx.strokeStyle = '#CCCCCC'
      ctx.lineWidth = 1
      for (let i = 0; i < indicators.length; i++) {
        const angle = angleStep * i - Math.PI / 2
        const x = centerX + radius * Math.cos(angle)
        const y = centerY + radius * Math.sin(angle)

        ctx.beginPath()
        ctx.moveTo(centerX, centerY)
        ctx.lineTo(x, y)
        ctx.stroke()
      }

      // 绘制指标文字
      ctx.font = '12px sans-serif'
      ctx.fillStyle = '#666666'
      ctx.textAlign = 'center'
      ctx.textBaseline = 'middle'
      for (let i = 0; i < indicators.length; i++) {
        const angle = angleStep * i - Math.PI / 2
        const cosAngle = Math.cos(angle)
        const sinAngle = Math.sin(angle)
        const isLeftOrRight = Math.abs(cosAngle) > 0.5
        const isBottom = sinAngle > 0.5
        let labelRadius = radius + 10
        if (isLeftOrRight) {
          labelRadius = radius + 45
        } else if (isBottom) {
          labelRadius = radius + 20
        }
        const x = centerX + labelRadius * Math.cos(angle)
        const y = centerY + labelRadius * Math.sin(angle)
        const value = Number.isFinite(score[i])
          ? Number.isInteger(score[i])
            ? `${score[i]}`
            : score[i].toFixed(1)
          : '--'
        const labelText = `${indicators[i]} ${value}`

        ctx.fillText(labelText, x, y)
      }

      // 绘制能力数据(青色填充)
      ctx.beginPath()
      ctx.strokeStyle = '#46B4B1'
      ctx.fillStyle = 'rgba(70, 180, 177, 0.2)'
      ctx.lineWidth = 2
      for (let i = 0; i < score.length; i++) {
        const angle = angleStep * i - Math.PI / 2
        const normalized = Number.isFinite(score[i]) ? Math.max(0, Math.min(1, score[i] / 10)) : 0
        const r = radius * normalized
        const x = centerX + r * Math.cos(angle)
        const y = centerY + r * Math.sin(angle)

        if (i === 0) {
          ctx.moveTo(x, y)
        } else {
          ctx.lineTo(x, y)
        }
      }
      ctx.closePath()
      ctx.fill()
      ctx.stroke()
    },

    // 选择批注
    selectAnnotation(index) {
      if (index >= 0 && index < this.parsedAnnotations.length) {
        this.currentAnnotationIndex = index
      }
    },

    // 处理批注点击事件
    handleAnnotationClick(annotationId) {
      // annotationId就是批注的索引
      this.selectAnnotation(annotationId)
      // 显示踩点详情弹窗
      this.showAnnotationModal = true
    },

    // 解析材料领读 JSON 数据
    parseMaterialReadingJson(materialReadingStr) {
      if (!materialReadingStr || typeof materialReadingStr !== 'string') {
        return
      }

      try {
        // 清理可能的 Markdown 代码块标记
        let cleanStr = materialReadingStr.trim()
        if (cleanStr.startsWith('```json')) {
          cleanStr = cleanStr.replace(/^```json\s*/, '').replace(/```\s*$/, '')
        } else if (cleanStr.startsWith('```')) {
          cleanStr = cleanStr.replace(/^```\s*/, '').replace(/```\s*$/, '')
        }
        cleanStr = cleanStr.trim()

        // 尝试解析 JSON
        const parsed = JSON.parse(cleanStr)

        // 验证数据结构并赋值
        if (parsed && typeof parsed === 'object') {
          // 材料梳理
          if (Array.isArray(parsed.materialAnalysis)) {
            this.aiAnalysisResults.materialReading.materialAnalysis = parsed.materialAnalysis
          }
          // 答题要点
          if (Array.isArray(parsed.answerPoints)) {
            this.aiAnalysisResults.materialReading.answerPoints = parsed.answerPoints
          }
          // 示范作答
          if (typeof parsed.sampleAnswer === 'string') {
            this.aiAnalysisResults.materialReading.sampleAnswer = parsed.sampleAnswer
          }
        }
      } catch (error) {
        // JSON 解析失败,保持使用旧的 Markdown 格式显示
        console.warn('材料领读 JSON 解析失败,将使用 Markdown 格式显示', error)
      }
    },

    // 滚动到指定的材料领读卡片
    scrollToReadingCard(stepIndex) {
      this.currentReadingStep = stepIndex

      const refNames = ['materialAnalysisCard', 'answerPointsCard', 'sampleAnswerCard']
      const refName = refNames[stepIndex]

      if (this.$refs[refName]) {
        this.$refs[refName].scrollIntoView({ behavior: 'smooth', block: 'start' })
      }
    },

    // 渲染Markdown - 简化版本,将Markdown文本转换为基本HTML
    renderMarkdown(content) {
      if (!content) return ''

      // 简单的Markdown转HTML处理
      let html = content
        // 转换标题
        .replace(/^### (.*$)/gim, '<h3>$1</h3>')
        .replace(/^## (.*$)/gim, '<h2>$1</h2>')
        .replace(/^# (.*$)/gim, '<h1>$1</h1>')
        // 转换粗体
        .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
        // 转换斜体
        .replace(/\*(.*?)\*/g, '<em>$1</em>')
        // 转换无序列表
        .replace(/^\s*[-*]\s+(.*$)/gim, '<li>$1</li>')
        // 转换换行
        .replace(/\n/g, '<br>')

      // 包裹列表项
      html = html.replace(/(<li>.*<\/li>)/s, '<ul>$1</ul>')

      return html
    }
  }
}
</script>

<style scoped lang="scss">
.ai-assistant-page {
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  width: 100%;
  background: #f7faf9;
}

/* 自定义导航栏 */
.custom-navbar {
  position: sticky;
  top: 0;
  z-index: 100;
  background: #f7faf9;
  padding: 12px 16px;
  display: flex;
  justify-content: center;
  align-items: center;

  .nav-tabs {
    width: 280px;
    height: 36px;
    border-radius: 10px;
    border: 1px solid #d9d9d9;
    font-size: 14px;
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 0 4px;
    gap: 3px;

    .nav-tab {
      flex: 1;
      height: 28px;
      border-radius: 8px;
      display: flex;
      justify-content: center;
      align-items: center;
      transition: all 0.3s ease;
      cursor: pointer;
      font-size: 14px;
      color: #666;
      padding: 0 2px;
      text-align: center;

      &:hover {
        background-color: rgba(70, 180, 177, 0.1);
      }

      &.active {
        background-color: #46b4b1;
        color: #ffffff;
        transform: scale(1.05);
        box-shadow: 0 2px 8px rgba(70, 180, 177, 0.3);
      }
    }
  }
}

/* 内容容器 */
.content-container {
  flex: 1;
  padding: 0 16px 16px 16px;
  width: 100%;
  box-sizing: border-box;
  // 移除 overflow-y，让 el-dialog__body 作为滚动容器，使 sticky 定位生效
}

/* 全局AI提示词 */
.global-ai-tag {
  margin-bottom: 16px;

  span {
    font-size: 12px;
    color: #999;
  }
}

/* AI综合答案区域样式 */
.ai-comprehensive-answer-section {
  .comprehensive-answer-card {
    background: #fff;
    border-radius: 12px;
    padding: 16px;
    margin-bottom: 16px;

    .comprehensive-header {
      display: flex;
      align-items: center;
      justify-content: space-between;
      padding: 0 4px;
      margin-bottom: 16px;

      .comprehensive-title {
        font-size: 18px;
        font-weight: 600;
        color: #333;

        &.module-title {
          position: relative;
          display: inline-block;
          background: #fff;
          z-index: 20;
          padding-bottom: 4px;
          &::before {
            content: '';
            position: absolute;
            bottom: 8px;
            left: 50%;
            transform: translateX(-50%);
            width: calc(100% + 10px);
            height: 7px;
            background: rgba(78, 205, 196, 0.8);
            z-index: -1;
            border-radius: 3px;
          }
        }
      }

      .percentage-circle {
        display: flex;
        align-items: center;
        justify-content: center;

        .circle-container {
          position: relative;
          width: 80px;
          height: 80px;

          .progress-ring {
            position: absolute;
            top: 0;
            left: 0;
            width: 100%;
            height: 100%;
            border-radius: 50%;
            background: conic-gradient(
              from 225deg,
              #46b4b1 0deg,
              #46b4b1 var(--progress-angle, 0deg),
              #e5e5e5 var(--progress-angle, 0deg),
              #e5e5e5 270deg,
              transparent 270deg
            );
            transition: all 1s ease-out;

            &::after {
              content: '';
              position: absolute;
              top: 10px;
              left: 10px;
              width: 60px;
              height: 60px;
              border-radius: 50%;
              background: #fff;
            }
          }

          .circle-content {
            position: absolute;
            top: 50%;
            left: 50%;
            transform: translate(-50%, -50%);
            text-align: center;
            z-index: 1;

            .percent-text {
              font-size: 24px;
              font-weight: 700;
              color: #46b4b1;
              line-height: 1;
              margin-bottom: 4px;
            }

            .percent-label {
              font-size: 11px;
              color: #666;
              line-height: 1;
            }
          }
        }
      }
    }

    .comprehensive-content {
      .placeholder-content {
        padding: 40px 20px;
        text-align: center;

        .placeholder-text {
          font-size: 16px;
          color: #999;
          margin-bottom: 8px;
        }
      }

      .scoring-checklist-content,
      .analysis-markdown {
        font-size: 14px;
        line-height: 1.6;
        color: #555;
      }
    }

    // 用户答案区域样式
    .user-answer-content {
      font-size: 14px;
      color: #555;
      line-height: 1.6;
      margin-top: 16px;

      .plain-text {
        font-size: 14px;
        color: #555;
        line-height: 1.6;
        white-space: pre-wrap;
        word-break: break-word;
      }
    }
  }
}

/* 遗漏得分点卡片样式 */
.missed-points-list {
  display: flex;
  flex-direction: column;
  width: 100%;
  box-sizing: border-box;
}

.missed-point-item {
  font-size: 14px;
  line-height: 1.6;
  width: 100%;
  box-sizing: border-box;
  margin-bottom: 12px;
}

.point-header {
  display: flex;
  align-items: flex-start;
  margin-bottom: 12px;
  width: 100%;
  box-sizing: border-box;
}

.point-label {
  display: flex;
  align-items: center;
  flex-shrink: 0;
}

.point-prefix {
  font-size: 14px;
  font-weight: 600;
  color: #666;
}

.point-number {
  font-size: 14px;
  font-weight: 600;
  color: #46b4b1;
  flex-shrink: 0;
}

.point-content-wrapper {
  display: flex;
  align-items: flex-start;
  flex: 1;
  min-width: 0;
}

.point-content {
  font-size: 14px;
  color: #555;
  line-height: 1.5;
  word-wrap: break-word;
  word-break: break-all;
  flex: 1;
}

.point-suggestion {
  display: flex;
  align-items: flex-start;
  width: 100%;
  box-sizing: border-box;
  gap: 4px;
}

.suggestion-label {
  font-size: 14px;
  font-weight: 600;
  color: #666;
  flex-shrink: 0;
}

.suggestion-text {
  font-size: 14px;
  color: #555;
  line-height: 1.5;
  word-wrap: break-word;
  word-break: break-all;
  flex: 1;
  min-width: 0;
}

/* 材料领读页面 */
.reading-section {
  .reading-content {
    .reading-ai-result {
      background: #fff;
      border-radius: 12px;
      padding: 16px;
      margin-bottom: 0;

      .reading-ai-content {
        .reading-ai-markdown {
          font-size: 14px;
          line-height: 1.6;
          color: #334155;
        }

        .no-content {
          text-align: center;
          padding: 20px;
          color: #999;
          font-size: 14px;
        }
      }
    }

    // 结构化材料领读样式
    .structured-reading {
      // 步骤条样式
      .reading-steps {
        display: flex;
        position: sticky;
        top: 60px;
        z-index: 50;
        align-items: center;
        background: #fff;
        border-radius: 12px;
        padding: 16px;
        margin-bottom: 16px;
        gap: 8px;
        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);

        .step-item {
          display: flex;
          flex-direction: row;
          align-items: center;
          justify-content: center;
          gap: 6px;
          cursor: pointer;
          transition: all 0.3s ease;
          flex-shrink: 0;
          width: auto;

          &:hover {
            transform: scale(1.05);
          }

          .step-number {
            width: 32px;
            height: 32px;
            border-radius: 50%;
            background: #f5f5f5;
            color: #999;
            display: flex;
            align-items: center;
            justify-content: center;
            font-size: 16px;
            font-weight: 600;
            transition: all 0.3s ease;
          }

          .step-label {
            font-size: 13px;
            color: #666;
            text-align: center;
            transition: all 0.3s ease;
            white-space: nowrap;
          }

          &.active {
            .step-number {
              background: #46b4b1;
              color: #fff;
              box-shadow: 0 2px 8px rgba(70, 180, 177, 0.3);
            }

            .step-label {
              color: #46b4b1;
              font-weight: 600;
            }
          }
        }

        .step-line {
          flex: 1;
          height: 0;
          border-top: 1px dashed #d0d0d0;
          min-width: 10px;
        }
      }

      .reading-card {
        background: #fff;
        border-radius: 12px;
        padding: 16px;
        margin-bottom: 16px;

        &:last-child {
          margin-bottom: 0;
        }

        .reading-card-header {
          margin-bottom: 16px;
          padding: 0 4px;
          position: relative;

          .module-title {
            font-size: 18px;
            font-weight: 600;
            color: #333;
            padding-bottom: 4px;
            position: relative;
            display: inline-block;
            background: #fff;
            z-index: 20;

            &::before {
              content: '';
              position: absolute;
              bottom: 8px;
              left: 50%;
              transform: translateX(-50%);
              width: calc(100% + 10px);
              height: 7px;
              background: rgba(78, 205, 196, 0.8);
              z-index: -1;
              border-radius: 3px;
            }
          }
        }

        .reading-card-content {
          // 占位符样式
          .placeholder-content {
            padding: 40px 20px;
            text-align: center;

            .placeholder-text {
              font-size: 16px;
              color: #999;
              margin-bottom: 8px;
            }
          }

          // 材料梳理项样式
          .material-item {
            margin-bottom: 20px;
            padding-bottom: 20px;
            border-bottom: 1px dashed #e5e5e5;

            &:last-child {
              margin-bottom: 0;
              padding-bottom: 0;
              border-bottom: none;
            }

            .material-paragraph-label {
              font-size: 14px;
              font-weight: 600;
              color: #46b4b1;
              margin-bottom: 8px;
              padding: 4px 10px;
              background: rgba(70, 180, 177, 0.1);
              border-radius: 4px;
              display: inline-block;
            }

            .material-original-text {
              font-size: 14px;
              color: #333;
              line-height: 1.8;
              margin-bottom: 12px;
              padding: 8px 0;
              text-indent: 2em;
            }

            .material-key-points {
              display: flex;
              flex-wrap: wrap;
              align-items: center;
              margin-bottom: 8px;

              .key-points-label {
                font-size: 13px;
                font-weight: 600;
                color: #666;
                margin-right: 8px;
                flex-shrink: 0;
              }

              .key-point-tag {
                display: inline-block;
                padding: 4px 12px;
                background: #f5f5f5;
                color: #333;
                border-radius: 20px;
                font-size: 13px;
                margin-right: 8px;
                margin-bottom: 6px;
                font-weight: 500;
                box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
              }
            }

            .material-summary {
              display: flex;
              align-items: flex-start;

              .summary-label {
                font-size: 13px;
                font-weight: 600;
                color: #666;
                margin-right: 6px;
                flex-shrink: 0;
              }

              .summary-text {
                font-size: 13px;
                color: #555;
                line-height: 1.6;
                flex: 1;
              }
            }
          }

          // 答题要点项样式
          .answer-point-item {
            margin-bottom: 20px;

            &:last-child {
              margin-bottom: 0;
            }

            .point-dimension {
              font-size: 16px;
              font-weight: 600;
              color: #46b4b1;
              margin-bottom: 10px;
              padding-bottom: 8px;
              border-bottom: 1px solid rgba(70, 180, 177, 0.2);
            }

            .point-sources {
              display: flex;
              align-items: flex-start;
              margin-bottom: 10px;

              .sources-label {
                font-size: 13px;
                font-weight: 600;
                color: #666;
                margin-right: 6px;
                flex-shrink: 0;
              }

              .sources-text {
                font-size: 13px;
                color: #555;
                line-height: 1.6;
                flex: 1;
              }
            }

            .point-content-text {
              font-size: 14px;
              color: #333;
              line-height: 1.8;
            }
          }

          // 示范作答样式
          .sample-answer-text {
            font-size: 14px;
            color: #333;
            line-height: 1.8;
            white-space: pre-wrap;
            word-wrap: break-word;
          }
        }
      }
    }
  }
}

/* 优化建议页面 */
.optimization-section {
  // 通用模块样式
  .module-header {
    margin-bottom: 16px;
    padding: 0 4px;
    position: relative;

    .module-title {
      font-size: 18px;
      font-weight: 600;
      color: #333;
      padding-bottom: 4px;
      position: relative;
      display: inline-block;
      background: #fff;
      z-index: 20;
    }

    .module-title::before {
      content: '';
      position: absolute;
      bottom: 8px;
      left: 50%;
      transform: translateX(-50%);
      width: calc(100% + 10px);
      height: 7px;
      background: rgba(78, 205, 196, 0.8);
      z-index: -1;
      border-radius: 3px;
    }
  }

  // 各模块卡片基本样式
  .ability-radar,
  .structured-advice,
  .personalized-recommendation {
    background: #fff;
    border-radius: 12px;
    padding: 16px;
    margin-bottom: 16px;
  }

  .ability-radar {
    .module-header {
      margin-bottom: 0;
    }

    .radar-content {
      width: 100%;
      height: 300px;
      display: flex;
      justify-content: center;
      align-items: center;

      .radar-canvas {
        width: 100%;
        height: 300px;
      }
    }
  }

  .structured-advice {
    .structured-content {
      .optimization-markdown {
        background: #f8f9fa;
        border-radius: 8px;
        font-size: 14px;
        line-height: 1.8;
        color: #333;
        padding: 12px;
      }

      .placeholder-content {
        padding: 40px 20px;
        text-align: center;

        .placeholder-text {
          font-size: 16px;
          color: #999;
          margin-bottom: 8px;
        }
      }
    }
  }

  .personalized-recommendation {
    .recommendation-content {
      .recommendation-markdown {
        background: #f8f9fa;
        border-radius: 8px;
        font-size: 14px;
        line-height: 1.8;
        color: #333;
        padding: 12px;
      }

      .placeholder-content {
        padding: 40px 20px;
        text-align: center;

        .placeholder-text {
          font-size: 16px;
          color: #999;
          margin-bottom: 8px;
        }
      }
    }
  }
}

/* 踩点详情抽屉样式 */
::v-deep .annotation-drawer {
  background-color: #fff !important;

  .el-drawer__header {
    margin-bottom: 0;
    padding: 16px 20px;
    border-bottom: 1px solid #f0f0f0;
    background-color: #fff !important;
  }

  .el-drawer__body {
    padding: 0;
    background-color: #fff !important;
    overflow-y: auto;
  }

  .el-drawer__container {
    background-color: #fff !important;
  }
}

.drawer-header {
  .drawer-title {
    font-size: 18px;
    font-weight: 600;
    color: #333;
    text-align: center;
    width: 100%;
  }
}

.drawer-content {
  padding: 20px;
  background-color: #fff;

  .annotation-tooltip-container {
    background: #fff;
    font-size: 14px;
    line-height: 1.6;
  }

  .tooltip-content {
    .original-content,
    .correction-content,
    .suggestion-content {
      margin-bottom: 16px;
      font-size: 14px;
    }

    .content-label {
      display: block;
      font-weight: 600;
      color: #666;
      margin-bottom: 6px;
      font-size: 14px;
    }

    .content-text {
      color: #333;
      line-height: 1.6;
      word-wrap: break-word;
      font-size: 14px;
    }

    .suggestion-label {
      display: block;
      font-weight: 600;
      color: #666;
      margin-bottom: 8px;
      font-size: 14px;
    }

    .suggestion-list {
      display: flex;
      flex-direction: column;
      gap: 8px;
    }

    .suggestion-item {
      padding-left: 16px;
      position: relative;
      font-size: 14px;

      &::before {
        content: '•';
        position: absolute;
        left: 0;
        color: #46b4b1;
        font-weight: bold;
        font-size: 14px;
      }
    }

    .suggestion-text {
      color: #333;
      line-height: 1.6;
      font-size: 14px;
    }
  }
}
</style>
